import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import { useParams } from "react-router-dom";
import PageLoadingSpinner from "components/common/PageLoader";
import BreadCrumb from "components/common/BreadCrumb";
import { getDocumentTitle } from "utils/helpers";
import { getPurchaseOrder } from "../services/PurchaseOrderService";
import type { PurchaseOrder } from "../types/purchase-order.types";
import PurchaseOrderOverviewContext from "./PurchaseOrderOverviewContext";
import Section from "./Section";

const PurchaseOrderOverview = () => {
  document.title = getDocumentTitle("Purchase Order Details");
  const { id } = useParams();
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!id) return;
    const res = await getPurchaseOrder(Number(id));
    if (res.success && res.data) setPurchaseOrder(res.data);
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await getPurchaseOrder(Number(id));
        if (!cancelled && res.success && res.data) {
          setPurchaseOrder(res.data);
        } else if (!cancelled) {
          setPurchaseOrder(null);
        }
      } catch {
        if (!cancelled) setPurchaseOrder(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return <PageLoadingSpinner message="Loading purchase order…" />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Purchase order details"
          pageTitle="Inventory Management"
          items={[
            {
              label: "Purchase orders",
              path: "/inventory-management/purchase-orders",
            },
          ]}
        />
        <PurchaseOrderOverviewContext.Provider
          value={{ purchaseOrder, refetch }}
        >
          <Section />
        </PurchaseOrderOverviewContext.Provider>
      </Container>
    </div>
  );
};

export default PurchaseOrderOverview;
